import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Edit2, Save, Trash2, X } from 'react-feather';
import moment from 'moment';
import { COLORS } from '../../../../utils/colors';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  updateApplicationUpdateHistory,
  updateSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { getMentionableUsers } from '../../../../redux/services/authService';
import { openDillToast } from '../../../../utils/helpers';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillCheckbox } from '../../../general/Dill';
import { Plus } from 'phosphor-react';
import MentionTextarea, { MentionCandidate, TagPeopleButton } from './MentionTextarea';

const CreditAppNotes = ({
  notes,
  creditAppId,
  isSalesRole,
  applicationHistoryId,
  onNoteAdded,
  defaultOpen,
  title,
  hideHeaderCount,
  collapseComposerOnly,
  onSaveNotes,
}: {
  notes: {
    note: string;
    date?: Date | null;
    submittedBy?: string | null;
    visibleToSales?: boolean;
    fromSales?: boolean;
    mentionedUserIds?: string[];
  }[];
  creditAppId: string;
  isSalesRole?: boolean;
  applicationHistoryId?: string | null;
  /** Called after a note is successfully submitted, e.g. to close a host modal. */
  onNoteAdded?: () => void;
  /** Force the panel open on mount regardless of whether there are existing notes —
   * e.g. the table's Notes modal, where a collapsed panel with nothing to click would
   * strand someone trying to add the very first note. Defaults to open-if-there-are-any. */
  defaultOpen?: boolean;
  /** Header label. Defaults to "Internal Notes". */
  title?: string;
  /** Hide the note-count badge in this component's own header — e.g. when a host modal
   * already shows the count in its own title bar. */
  hideHeaderCount?: boolean;
  /** Only collapse the composer (the "Write a note..." input) behind the header toggle;
   * the notes list stays visible regardless of collapse state. Defaults to collapsing
   * the composer and the list together. */
  collapseComposerOnly?: boolean;
  onSaveNotes?: (
    notes: {
      note: string;
      date: Date;
      submittedBy?: string | null;
      visibleToSales?: boolean;
      fromSales?: boolean;
      mentionedUserIds?: string[];
    }[]
  ) => Promise<boolean>;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const mentionableUsers = useAppSelector((state) => state.auth.mentionableUsers);

  const [isDropdownOpen, setIsDropdownOpen] = useState(defaultOpen ?? notes.length > 0);
  const [editableNotes, setEditableNotes] = useState(notes);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedText, setEditedText] = useState('');
  const [editMentions, setEditMentions] = useState<MentionCandidate[]>([]);
  const [newNote, setNewNote] = useState('');
  const [newNoteMentions, setNewNoteMentions] = useState<MentionCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNoteError, setNewNoteError] = useState('');
  const [visibleToSales, setVisibleToSales] = useState(false);

  useEffect(() => {
    setEditableNotes(notes);
  }, [notes]);

  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getMentionableUsers({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id]);

  // People who can be @tagged in a note — anyone with an account on this supplier.
  const candidates: MentionCandidate[] = useMemo(
    () =>
      mentionableUsers.map((u) => ({
        id: u.id,
        name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
        email: u.email,
      })),
    [mentionableUsers, user?.id]
  );
  const candidatesById = useMemo(() => new Map(candidates.map((c) => [c.id, c])), [candidates]);

  const dispatchNotesUpdate = async (
    updatedNotes: {
      note: string;
      date: Date;
      submittedBy?: string | null;
      visibleToSales?: boolean;
      fromSales?: boolean;
      mentionedUserIds?: string[];
    }[]
  ): Promise<boolean> => {
    if (onSaveNotes) return onSaveNotes(updatedNotes);
    if (applicationHistoryId) {
      const result = await dispatch(
        updateApplicationUpdateHistory({
          creditApplicationId: creditAppId,
          applicationHistoryId,
          internalNotes: updatedNotes,
          supplierId: currentSupplier?.id || '',
        })
      );
      return result.type.includes('fulfilled');
    }
    const result = await dispatch(
      updateSupplierCreditApplication({
        creditApplicationId: creditAppId,
        internalNotes: updatedNotes,
        supplierId: currentSupplier?.id || '',
      })
    );
    return result.type.includes('fulfilled');
  };

  const handleDeleteNote = async (index: number) => {
    const updated = editableNotes.filter((_, i) => i !== index);
    setEditableNotes(updated);
    const internalNotes = updated.map((item) => ({
      ...item,
      date: new Date(item?.date ?? ''),
    }));
    const isSaved = await dispatchNotesUpdate(internalNotes);
    if (isSaved) {
      openDillToast({
        type: 'SUCCESS',
        message: 'Successfully removed internal note.',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to remove internal note.',
      });
    }
  };

  const handleEditNote = (index: number) => {
    setEditingIndex(index);
    setNewNote('');
    setNewNoteError('');
    setEditedText(editableNotes[index].note);
    const existingMentions = (editableNotes[index].mentionedUserIds ?? [])
      .map((id) => candidatesById.get(id))
      .filter((c): c is MentionCandidate => !!c);
    setEditMentions(existingMentions);
  };

  const handleSaveNote = async (index: number) => {
    const updated = [...editableNotes];
    updated[index] = {
      ...updated[index],
      note: editedText.trim(),
      mentionedUserIds: editMentions.map((m) => m.id),
    };
    setEditableNotes(updated);
    setEditingIndex(null);
    setEditedText('');
    setEditMentions([]);
    const internalNotes = updated.map((item) => ({
      ...item,
      date: new Date(item?.date ?? ''),
    }));
    const isSaved = await dispatchNotesUpdate(internalNotes);
    if (isSaved) {
      openDillToast({
        type: 'SUCCESS',
        message: 'Successfully updated internal note.',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to update internal note.',
      });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setNewNoteError('Please enter a note.');
      return;
    }
    setLoading(true);
    const newNoteObj = {
      note: newNote.trim(),
      date: new Date(),
      submittedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || null,
      // Sales-authored notes are always visible to Sales (it's their own note) and to Credit
      // (Credit sees every note); only Credit's "Visible to Sales" checkbox is user-controlled.
      visibleToSales: isSalesRole ? true : visibleToSales,
      fromSales: isSalesRole,
      mentionedUserIds: newNoteMentions.map((m) => m.id),
    };
    const updatedNotes = [...editableNotes, newNoteObj];
    const isSaved = await dispatchNotesUpdate(
      updatedNotes.map((item) => ({ ...item, date: new Date(item?.date ?? '') }))
    );
    if (isSaved) {
      openDillToast({ type: 'SUCCESS', message: 'Note added.' });
      setNewNote('');
      setNewNoteMentions([]);
      setVisibleToSales(false);
      setIsDropdownOpen(true);
      onNoteAdded?.();
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to add note.' });
    }
    setLoading(false);
  };

  // Credit sees every note. Sales only sees notes explicitly shared with them
  // (visibleToSales) plus notes they authored themselves (fromSales).
  const displayedNotes = isSalesRole
    ? editableNotes.filter((n) => n.visibleToSales || n.fromSales)
    : editableNotes;

  // Credit can edit/delete any note. Sales can only edit/delete their own notes.
  const canModify = (item: { fromSales?: boolean }) => !isSalesRole || !!item.fromSales;

  const initials = (name: string) =>
    name
      .split(' ')
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase() ?? '')
      .join('');

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // The tagged-names list a note's "@Name" runs get matched against — this note's own
  // tagged people plus every candidate (older notes may have "@Name" baked into the text
  // for someone not in mentionedUserIds, from before tagging stopped touching note text).
  const mentionNamesFor = (mentionedUserIds?: string[]) => {
    const taggedNames = (mentionedUserIds ?? [])
      .map((id) => candidatesById.get(id)?.name)
      .filter((n): n is string => !!n);
    return [...new Set([...taggedNames, ...candidates.map((c) => c.name)])];
  };

  // Highlights each "@Name" run inline as a pill, matching how it's shown while composing,
  // instead of stripping it out of the text.
  const renderNoteBody = (text: string, names: string[]): React.ReactNode => {
    if (names.length === 0) return text;
    const pattern = new RegExp(
      `@(?:${[...names]
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp)
        .join('|')})`,
      'g'
    );
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;
    while ((match = pattern.exec(text))) {
      if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
      nodes.push(
        <span key={key++} className="rounded bg-blue-100 text-blue-600">
          {match[0]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes;
  };

  // Tagged people with no "@Name" anchor in the note text (carried over from before inline
  // mentions, or tagged via the "tag someone" button) — nowhere inline to show them, so
  // they still get a chip below the note.
  const chipOnlyMentions = (note: string, mentionedUserIds?: string[]) =>
    (mentionedUserIds ?? [])
      .map((id) => candidatesById.get(id))
      .filter((c): c is MentionCandidate => !!c && !note.includes(`@${c.name}`));

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={(e) => {
          if (e.defaultPrevented) return;
          setIsDropdownOpen((o) => !o);
          if (isDropdownOpen) {
            setNewNote('');
            setEditingIndex(null);
          }
        }}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">{title ?? 'Internal Notes'}</span>
          {!hideHeaderCount && displayedNotes.length > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">
              {displayedNotes.length}
            </span>
          )}
        </div>
        <div
          className={`transition-transform duration-200 ${
            isDropdownOpen ? 'rotate-180' : 'rotate-0'
          }`}>
          <ChevronDown size={16} color={COLORS.GREY_500} />
        </div>
      </div>

      {(isDropdownOpen || collapseComposerOnly) && (
        <div className="mt-3 flex flex-col gap-3">
          {/* Composer — Credit can write private or Sales-visible notes; Sales can write
              notes back to Credit (always visible to Credit, and to Sales themselves).
              When collapseComposerOnly is set, only this piece hides behind the toggle;
              the list below stays visible either way. */}
          {isDropdownOpen && (
            <div
              className={`rounded-xl border bg-white p-3 transition-colors ${
                newNoteError ? 'border-red-300' : 'border-gray-200 focus-within:border-blue-300'
              }`}>
              <MentionTextarea
                value={newNote}
                onChange={(val) => {
                  setNewNote(val);
                  setEditingIndex(null);
                  setNewNoteError('');
                }}
                candidates={candidates}
                taggedUsers={newNoteMentions}
                onTaggedUsersChange={setNewNoteMentions}
                placeholder={
                  isSalesRole
                    ? 'Write a note to Credit... (@ to tag someone)'
                    : 'Write a note... (@ to tag someone)'
                }
                className="w-full text-xs text-gray-700 placeholder-gray-400 resize-none focus:outline-none bg-transparent leading-relaxed"
                rows={2}
              />
              {newNoteError && <p className="text-[10px] text-red-500 mt-0.5">{newNoteError}</p>}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                {isSalesRole ? (
                  <div />
                ) : (
                  <div
                    className="flex items-center gap-1.5 cursor-pointer"
                    onClick={(e) => e.preventDefault()}>
                    <DillCheckbox
                      onChange={(checked: boolean) => setVisibleToSales(checked)}
                      isChecked={visibleToSales}
                    />
                    <span className="text-[11px] text-gray-500">Visible to Sales</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <TagPeopleButton
                    candidates={candidates}
                    taggedUsers={newNoteMentions}
                    onTaggedUsersChange={setNewNoteMentions}
                  />
                  <DillButton
                    icon={<Plus size={12} />}
                    variant="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddNote();
                    }}
                    label="Add"
                    height={26}
                    isLoading={loading}
                    isDisabled={loading}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes list */}
          {displayedNotes.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2 italic">No notes yet.</p>
          ) : (
            <div className="flex flex-col gap-0 max-h-64 overflow-y-auto pr-0.5">
              {editableNotes
                .map((item, originalIndex) => ({ item, originalIndex }))
                .filter(({ item }) => !isSalesRole || item.visibleToSales || item.fromSales)
                .reverse()
                .map(({ item, originalIndex }, displayIndex, arr) => {
                  const i = originalIndex;
                  const isLast = displayIndex === arr.length - 1;
                  return (
                    <div key={`note_${i}`} className="flex gap-2.5 pb-3 last:pb-0 group">
                      {/* Avatar + connector */}
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] font-bold shrink-0">
                          {initials(item.submittedBy ?? '')}
                        </div>
                        {!isLast && <div className="w-px flex-1 bg-gray-100 mt-1" />}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        {/* Meta row */}
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-[11px] font-semibold text-gray-700 truncate">
                              {item.submittedBy ?? '—'}
                            </span>
                            {item.fromSales ? (
                              <span className="shrink-0 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[9px] font-medium text-amber-600">
                                From Sales
                              </span>
                            ) : (
                              item.visibleToSales && (
                                <span className="shrink-0 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-px text-[9px] font-medium text-blue-600">
                                  Visible to Sales
                                </span>
                              )
                            )}
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <span className="text-[10px] text-gray-400">
                              {item.date ? moment(item.date).format('MM/DD/YY LT') : ''}
                            </span>
                            {canModify(item) && editingIndex === i ? (
                              <>
                                <button
                                  onClick={() => handleSaveNote(i)}
                                  data-tooltip-id="save-note"
                                  className="ml-1 p-0.5 rounded text-blue-500 hover:bg-blue-50 cursor-pointer border-none bg-transparent">
                                  <Save size={11} />
                                </button>
                                <button
                                  onClick={() => setEditingIndex(null)}
                                  className="p-0.5 rounded text-gray-400 hover:bg-gray-100 cursor-pointer border-none bg-transparent">
                                  <X size={11} />
                                </button>
                              </>
                            ) : (
                              canModify(item) && (
                                <div className="flex items-center gap-0.5 group-hover:opacity-100 transition-opacity ml-1">
                                  <button
                                    onClick={() => handleEditNote(i)}
                                    data-tooltip-id="edit-note"
                                    className="p-0.5 rounded text-gray-300 hover:text-blue-500 hover:bg-blue-50 cursor-pointer border-none bg-transparent">
                                    <Edit2 size={11} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteNote(i)}
                                    data-tooltip-id="delete-note"
                                    className="p-0.5 rounded text-gray-300 hover:text-red-400 hover:bg-red-50 cursor-pointer border-none bg-transparent">
                                    <Trash2 size={11} />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        {/* Note body */}
                        {editingIndex === i ? (
                          <MentionTextarea
                            value={editedText}
                            onChange={setEditedText}
                            candidates={candidates}
                            taggedUsers={editMentions}
                            onTaggedUsersChange={setEditMentions}
                            className="w-full bg-white border border-blue-200 rounded-lg text-xs p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-300 leading-relaxed"
                            rows={2}
                            autoFocus
                          />
                        ) : (
                          <>
                            <p
                              className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed"
                              onDoubleClick={() => canModify(item) && handleEditNote(i)}>
                              {renderNoteBody(item.note, mentionNamesFor(item.mentionedUserIds))}
                            </p>
                            {chipOnlyMentions(item.note, item.mentionedUserIds).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {chipOnlyMentions(item.note, item.mentionedUserIds).map((c) => (
                                  <span
                                    key={c.id}
                                    className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                                    @{c.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      <ReactTooltip
        id="edit-note"
        place="top"
        variant="dark"
        opacity="100%"
        style={{ background: '#334155', borderRadius: '6px', fontSize: '11px', zIndex: 70 }}>
        <span>Edit</span>
      </ReactTooltip>
      <ReactTooltip
        id="save-note"
        place="top"
        variant="dark"
        opacity="100%"
        style={{ background: '#334155', borderRadius: '6px', fontSize: '11px', zIndex: 70 }}>
        <span>Save</span>
      </ReactTooltip>
      <ReactTooltip
        id="delete-note"
        place="top"
        variant="dark"
        opacity="100%"
        style={{ background: '#334155', borderRadius: '6px', fontSize: '11px', zIndex: 70 }}>
        <span>Delete</span>
      </ReactTooltip>
    </div>
  );
};

export default CreditAppNotes;
